import classNames from 'clsx';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getDescriptorId, isFileWarning } from 'reactApp/modules/uploadList/uploadList.getters';
import { EFileError, EInfoTypes, IUploaderInformerItem } from 'reactApp/modules/uploadList/uploadList.model';
import {
    hideInformer,
    setHasConnectionError,
    setSelectedErrorAction,
    updateUploadFilesAction,
} from 'reactApp/modules/uploadList/uploadList.module';
import {
    getHighlightSelectedErrorFile,
    getSelectedErrorId,
    hasConnectionErrorFiles,
} from 'reactApp/modules/uploadList/uploadList.selectors';
import { Arrows } from 'reactApp/ui/UploadList/Problems/Arrows';
import { CloseIcon } from 'reactApp/ui/UploadList/Problems/CloseIcon';
import { Problem } from 'reactApp/ui/UploadList/Problems/Problem';

import styles from './Problems.css';

interface ProblemsContainerProps {
    informers: IUploaderInformerItem[];
}

const calculateNextIndex = (fileIndex: number, problemFilesLength: number, back: boolean) => {
    const incrementedIdx = fileIndex + 1;
    const decrementedIdx = fileIndex - 1;

    if (back) {
        return decrementedIdx < 0 ? problemFilesLength - 1 : decrementedIdx;
    }

    return incrementedIdx >= problemFilesLength ? 0 : incrementedIdx;
};

const closableInformers = [EInfoTypes.complete, EInfoTypes.lowSpeed];

export const ProblemsContainer = memo(({ informers }: ProblemsContainerProps) => {
    const [maxHeight, setMaxHeight] = useState(0);
    const [prevActive, setPrevActive] = useState(false);
    const [nextActive, setNextActive] = useState(false);
    const [fileIndex, setFileIndex] = useState(0);
    const hasConnectionError = useSelector(hasConnectionErrorFiles);
    const selectedId = useSelector(getSelectedErrorId);
    const highlightSelectedFile = useSelector(getHighlightSelectedErrorFile);
    const ref = useRef<HTMLDivElement | null>(null);
    const dispatch = useDispatch();

    const informer = informers[fileIndex];
    const problemFile = informer?.file;

    const problemFilesLength = informers.length;

    const showArrows = !isFileWarning(problemFile) && informers.length > 1;

    const showClose =
        (problemFile && problemFile.error !== EFileError.CONNECTION_ERROR) ||
        informer?.type === EInfoTypes.networkRestored ||
        closableInformers.includes(informer?.type);

    const switchWarning = useCallback(
        ({ back, highlightFile }: { back: boolean; highlightFile: boolean }) => {
            const nextIndex = calculateNextIndex(fileIndex, problemFilesLength, back);

            dispatch(setSelectedErrorAction({ file: informers[nextIndex].file, fileId: informers[nextIndex].id }));

            const file = informers[nextIndex].file;

            if (!file) {
                return;
            }

            const cloudPath = file.cloudPath;
            const descriptorId = file.descriptorId;
            if (highlightFile) {
                dispatch(updateUploadFilesAction({ descriptorId, cloudPath, highlight: true }));
            }
        },
        [problemFilesLength, fileIndex, informers]
    );

    const onClose = useCallback(() => {
        if (informer.file) {
            dispatch(
                updateUploadFilesAction({
                    descriptorId: getDescriptorId(informer.file),
                    cloudPath: informer.file.cloudPath,
                    hideError: true,
                })
            );
            switchWarning({ back: true, highlightFile: false });

            return;
        }

        if (informer.type === EInfoTypes.networkRestored) {
            dispatch(setHasConnectionError(false));
        } else if (closableInformers.includes(informer.type)) {
            dispatch(hideInformer(informer.type));
        }
    }, [informer]);

    useEffect(() => {
        if (hasConnectionError) {
            dispatch(setHasConnectionError(true));
        }
    }, [hasConnectionError]);

    useEffect(() => {
        if (!selectedId && informers.length > 0) {
            dispatch(setSelectedErrorAction({ file: informers[0].file, fileId: informers[0].id }));
        }
    }, [selectedId, informers.length]);

    useEffect(() => {
        const findFileIndex = informers.findIndex((item) => item.id === selectedId);

        if (findFileIndex === -1) {
            return;
        }

        setPrevActive(findFileIndex !== 0);
        setNextActive(findFileIndex !== problemFilesLength - 1);
        setFileIndex(findFileIndex);
    }, [selectedId, informers.length]);

    useEffect(() => {
        if (!selectedId) {
            return;
        }

        const fileIndex = informers.findIndex((item) => item.id === selectedId);
        const problemFile = informers[fileIndex]?.file;

        if (!problemFile) {
            switchWarning({ back: true, highlightFile: false });
        }
    }, [informers]);

    useEffect(() => {
        if (!ref.current) {
            return;
        }

        const height = ref.current.getBoundingClientRect().height;

        if (height !== maxHeight) {
            setMaxHeight(height);
        }
    }, [maxHeight, selectedId]);

    return (
        <div
            className={classNames({
                [styles.root]: true,
                [styles.highlight]: highlightSelectedFile,
                [styles.root_arrows]: showArrows,
            })}
            style={{ height: `${maxHeight}px` }}
        >
            <div ref={ref} className={styles.items}>
                {informers.map((item) => (
                    <Problem key={item.id} item={item} show={item.id === selectedId} />
                ))}
            </div>
            {showClose && <CloseIcon onClose={onClose} />}
            {showArrows && (
                <Arrows
                    fileIndex={fileIndex}
                    fileLength={informers.length}
                    prevActive={prevActive}
                    nextActive={nextActive}
                    switchWarning={switchWarning}
                />
            )}
        </div>
    );
});

ProblemsContainer.displayName = 'ProblemsContainer';
